import type { NostrEvent } from "@/nostr/types"

/**
 * Language axis of the unified filter architecture.
 *
 * Detection is deliberately conservative and dependency free:
 *
 * 1. an explicit language tag wins (`["l", "en"]`, `["language", "en"]`, or a
 *    NIP-32 `["l", "en", "ISO-639-1"]` label);
 * 2. otherwise the dominant writing system is used (Japanese, Korean, Chinese,
 *    Cyrillic, Arabic, Hebrew, Greek, Thai, Devanagari);
 * 3. for Latin script a small stop-word model picks between the languages this
 *    client ships strings for.
 *
 * When nothing is conclusive `detectLanguage` returns `undefined` and
 * `matchesLanguageFilter` keeps the post: a missing language must never hide a
 * post, it is only reported as "undetected" in the UI.
 */

export interface LanguageOption {
  code: string
  label: string
}

/** Languages offered in the filter panel (BCP-47 primary subtags). */
export const LANGUAGE_OPTIONS: readonly LanguageOption[] = [
  { code: "en", label: "English" },
  { code: "es", label: "Spanish" },
  { code: "fr", label: "French" },
  { code: "de", label: "German" },
  { code: "pt", label: "Portuguese" },
  { code: "it", label: "Italian" },
  { code: "nl", label: "Dutch" },
  { code: "ru", label: "Russian" },
  { code: "uk", label: "Ukrainian" },
  { code: "pl", label: "Polish" },
  { code: "tr", label: "Turkish" },
  { code: "ja", label: "Japanese" },
  { code: "zh", label: "Chinese" },
  { code: "ko", label: "Korean" },
  { code: "ar", label: "Arabic" },
  { code: "he", label: "Hebrew" },
  { code: "fa", label: "Persian" },
  { code: "hi", label: "Hindi" },
  { code: "th", label: "Thai" },
  { code: "el", label: "Greek" },
]

const LANGUAGE_CODES = new Set(LANGUAGE_OPTIONS.map((option) => option.code))

/** `en-US`, `en_US` and `EN` all become `en`. */
export function primarySubtag(value: string | undefined): string | undefined {
  if (!value) return undefined
  const base = value.trim().toLowerCase().split(/[-_]/)[0]
  return base || undefined
}

const LANGUAGE_TAG_PATTERN = /^iso[-_]?639|^(language|lang)s?$/i

const SCRIPT_PATTERNS: Array<[string, RegExp]> = [
  // Kana first: Japanese mixes Han and Kana.
  ["ja", /[\u3040-\u30ff]/g],
  ["ko", /[\uac00-\ud7af]/g],
  ["zh", /[\u4e00-\u9fff]/g],
  ["ru", /[\u0400-\u04ff]/g],
  ["ar", /[\u0600-\u06ff]/g],
  ["he", /[\u0590-\u05ff]/g],
  ["el", /[\u0370-\u03ff]/g],
  ["th", /[\u0e00-\u0e7f]/g],
  ["hi", /[\u0900-\u097f]/g],
]

const MIN_SCRIPT_CHARS = 3

const LATIN_STOPWORDS: Record<string, string[]> = {
  en: ["the", "and", "you", "that", "with", "for", "this", "have", "are", "but", "not", "what", "your", "from", "they", "was", "were", "about", "just", "here", "will", "would"],
  es: ["que", "los", "las", "una", "por", "con", "para", "como", "pero", "más", "esto", "este", "muy", "porque", "cuando", "gracias", "también", "hay"],
  fr: ["les", "des", "une", "pour", "avec", "dans", "pas", "que", "qui", "sur", "plus", "mais", "comme", "vous", "nous", "être", "sont", "cette"],
  de: ["und", "der", "die", "das", "nicht", "mit", "für", "auch", "ist", "ich", "wir", "aber", "wenn", "noch", "oder", "wie", "sind", "eine"],
  pt: ["que", "não", "uma", "para", "com", "isso", "mais", "como", "você", "muito", "porque", "quando", "também", "está", "dos", "das", "são"],
  it: ["che", "non", "una", "per", "con", "questo", "sono", "più", "come", "anche", "quando", "perché", "molto", "gli", "della", "dei"],
  nl: ["het", "een", "niet", "van", "dat", "met", "voor", "maar", "ook", "zijn", "deze", "nog", "wel", "als", "heeft", "wordt"],
}

const STOPWORD_SETS = new Map(
  Object.entries(LATIN_STOPWORDS).map(([code, words]) => [code, new Set(words)] as const),
)

const LATIN_WORD = /[\p{Script=Latin}]+/gu
const MIN_LATIN_WORDS = 6
const MIN_LATIN_HITS = 3
const MIN_LATIN_RATIO = 0.25

function detectByScript(content: string): string | undefined {
  for (const [code, pattern] of SCRIPT_PATTERNS) {
    const matches = content.match(pattern)
    if (matches && matches.length >= MIN_SCRIPT_CHARS) return code
  }
  return undefined
}

function detectByStopwords(content: string): string | undefined {
  const tokens = content.toLowerCase().match(LATIN_WORD)
  if (!tokens || tokens.length < MIN_LATIN_WORDS) return undefined

  let bestCode: string | undefined
  let bestHits = 0
  let runnerUp = 0
  for (const [code, words] of STOPWORD_SETS) {
    let hits = 0
    for (const token of tokens) if (words.has(token)) hits += 1
    if (hits > bestHits) {
      runnerUp = bestHits
      bestHits = hits
      bestCode = code
    } else if (hits > runnerUp) {
      runnerUp = hits
    }
  }

  if (!bestCode) return undefined
  if (bestHits < MIN_LATIN_HITS) return undefined
  if (bestHits / tokens.length < MIN_LATIN_RATIO) return undefined
  // A tie between two languages is not a detection.
  if (bestHits === runnerUp) return undefined
  return bestCode
}

/** Same detection applied to a bare string (used when no tag is present). */
export function detectLanguageOfText(content: string): string | undefined {
  const text = (content ?? "").trim()
  if (!text) return undefined
  return detectByScript(text) ?? detectByStopwords(text)
}

/** Best-effort language of an event; `undefined` when it cannot be determined. */
export function detectLanguage(event: NostrEvent): string | undefined {
  return eventLanguageTag(event) ?? detectLanguageOfText(event.content)
}

/**
 * Language matching.
 *
 * - `"all"` shows everything;
 * - `"detected"` keeps only posts with a reliable detection;
 * - a specific code excludes posts detected as another language **and keeps
 *   posts whose language could not be determined**, so incomplete metadata never
 *   silently hides content.
 */
export function matchesLanguageFilter(event: NostrEvent, selection: string | undefined): boolean {
  if (!selection || selection === "all") return true
  const detected = detectLanguage(event)
  if (selection === "detected") return detected !== undefined
  if (detected === undefined) return true
  return detected === selection
}

/** Human readable label for a detected code. */
export function languageLabel(code: string): string {
  return LANGUAGE_OPTIONS.find((option) => option.code === code)?.label ?? code.toUpperCase()
}

/** The declared language of an event, when it carries a usable tag. */
export function eventLanguageTag(event: NostrEvent): string | undefined {
  for (const tag of event.tags) {
    if (tag[0] !== "l" && tag[0] !== "language" && tag[0] !== "lang") continue
    const namespace = tag[2]?.trim()
    // NIP-32 `l` tags carry a namespace; only language namespaces are trusted,
    // so ordinary labels are never mistaken for a language.
    if (namespace && !LANGUAGE_TAG_PATTERN.test(namespace)) continue
    const code = primarySubtag(tag[1])
    if (code && LANGUAGE_CODES.has(code)) return code
  }
  return undefined
}
